import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ITag } from '@shared/models/api/base';
import { IPrinterCreate, PrintService } from '@shared/services/print.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';

@Component({
	selector: 'app-create-printer',
	templateUrl: './create-printer.component.html',
	styleUrl: './create-printer.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreatePrinterComponent implements OnInit {
	@Output() created: EventEmitter<any> = new EventEmitter();
	@ViewChild('encodingInput', { static: false })
	encodingInput!: ElementRef<HTMLInputElement>;

	form: FormGroup;
	loading: boolean = false;
	printer: IPrinterCreate = {
		alias: '',
		description: '',
		encoding: '',
		printerPath: '',
		tags: [],
	};

	httpError: string = '';
	encodings: string[] = ['UTF-8', 'ASCII', 'windows-1252'];
	newTagName: string = '';
	newTagValue: string = '';
	tagError: string = '';

	constructor(
		public dialogRef: MatDialogRef<CreatePrinterComponent>,
		private printService: PrintService,
		private fb: FormBuilder,
		private translateService: TranslateService,
	) {}

	ngOnInit(): void {
		this.initializeForm();
	}

	onCancel = () => this.dialogRef.close();

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.printer.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			description: [this.printer.description, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			encoding: [this.printer.encoding, [Validators.required, Validators.maxLength(255)]],
			printerPath: [this.printer.printerPath, [Validators.required, Validators.maxLength(255)]],
		});
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				this.printer[key] = value[key];
			});
		});
	}

	openAutocomplete() {
		this.encodingInput.nativeElement.focus();
	}

	addTag(): void {
		const name = this.newTagName.trim();
		const value = this.newTagValue.trim();
		this.tagError = '';
		if (!name || !value) return;

		// Mirrors the backend PrinterValidator rule: tag names must be unique per printer,
		// case-insensitive - caught here instead of surfacing as a generic 400 at save time.
		if (this.printer.tags.some((tag) => tag.name.toLowerCase() === name.toLowerCase())) {
			this.tagError = 'mes.administration.printer.dialog.tags.duplicate';
			return;
		}

		this.printer.tags = [...this.printer.tags, { name, value }];
		this.newTagName = '';
		this.newTagValue = '';
	}

	removeTag(tag: ITag): void {
		this.printer.tags = this.printer.tags.filter((t) => t !== tag);
	}

	get controls() {
		return this.form.controls;
	}

	onSave() {
		if (this.loading) return;
		this.httpError = '';
		if (this.form.valid) {
			this.loading = true;

			this.printService.createPrinter(this.printer).subscribe({
				next: () => {
					this.created.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.httpError = `${errorResponse.status}`;
					this.loading = false;
				},
			});
		}
	}
}
